<template>
  <div class="q-pa-md">
    <!-- Приветствие -->
    <q-card class="q-mb-md">
      <q-card-section>
        <div class="row items-center">
          <div class="col">
            <div class="text-h5">{{ t('home.welcome', { name: user.name }) }}</div>
            <div class="text-subtitle2 text-grey">{{ t('home.your_role') }}: {{ getRoleText(user.role) }}</div>
          </div>
          <div class="col-auto">
            <q-chip
                :color="user.role === 'admin' ? 'primary' : 'secondary'"
                text-color="white"
                icon="person"
            >
              {{ getRoleText(user.role) }}
            </q-chip>
          </div>
        </div>
      </q-card-section>
    </q-card>

    <!-- Статистика и быстрые действия -->
    <div class="row q-gutter-md q-mb-md">
      <!-- Статистика -->
      <div class="col-md-8 col-xs-12">
        <q-card>
          <q-card-section>
            <div class="text-h6 q-mb-md">{{ t('home.overview.title') }}</div>

            <div class="row q-gutter-md">
              <div class="col">
                <q-card flat bordered>
                  <q-card-section class="text-center">
                    <q-icon name="facebook" size="md" color="primary" class="q-mb-sm" />
                    <div class="text-h4 text-primary">{{ stats.accounts }}</div>
                    <div class="text-caption">{{ t('home.overview.connected_accounts') }}</div>
                  </q-card-section>
                </q-card>
              </div>
              <div class="col">
                <q-card flat bordered>
                  <q-card-section class="text-center">
                    <q-icon name="track_changes" size="md" color="secondary" class="q-mb-sm" />
                    <div class="text-h4 text-secondary">{{ stats.pixels }}</div>
                    <div class="text-caption">{{ t('home.overview.active_pixels') }}</div>
                  </q-card-section>
                </q-card>
              </div>
              <div class="col">
                <q-card flat bordered>
                  <q-card-section class="text-center">
                    <q-icon name="send" size="md" color="positive" class="q-mb-sm" />
                    <div class="text-h4 text-positive">{{ stats.conversions }}</div>
                    <div class="text-caption">{{ t('home.overview.conversions_today') }}</div>
                  </q-card-section>
                </q-card>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>

      <!-- Быстрые действия -->
      <div class="col-md-4 col-xs-12">
        <q-card>
          <q-card-section>
            <div class="text-h6 q-mb-md">{{ t('home.quick_actions.title') }}</div>

            <div class="q-gutter-sm">
              <q-btn
                  color="primary"
                  icon="facebook"
                  :label="t('home.quick_actions.manage_accounts')"
                  to="/facebook-accounts"
                  class="full-width"
                  outline
              />

              <q-btn
                  color="secondary"
                  icon="analytics"
                  :label="t('home.quick_actions.conversion_history')"
                  to="/facebook-conversions"
                  class="full-width"
                  outline
              />

              <q-btn
                  v-if="user.role === 'admin'"
                  color="orange"
                  icon="group"
                  :label="t('home.quick_actions.manage_users')"
                  to="/admin/users"
                  class="full-width"
                  outline
              />
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- Быстрая отправка конверсии -->
    <div class="q-mb-md">
      <QuickConversionForm @conversion-sent="onConversionSent" />
    </div>

    <!-- Последние конверсии -->
    <q-card>
      <q-card-section>
        <div class="row items-center q-mb-md">
          <div class="col">
            <div class="text-h6">{{ t('home.recent_conversions.title') }}</div>
          </div>
          <div class="col-auto">
            <q-btn
                flat
                icon="refresh"
                @click="fetchRecentConversions"
                :loading="loadingConversions"
            >
              <q-tooltip>{{ t('common.refresh') }}</q-tooltip>
            </q-btn>
          </div>
        </div>

        <q-table
            :rows="recentConversions"
            :columns="conversionColumns"
            row-key="id"
            flat
            :pagination="{ rowsPerPage: 5 }"
            :loading="loadingConversions"
            :no-data-label="t('home.recent_conversions.no_data')"
        >
          <template v-slot:body-cell-status="props">
            <q-td :props="props">
              <q-chip
                  :color="getStatusColor(props.value)"
                  text-color="white"
                  size="sm"
                  :icon="getStatusIcon(props.value)"
              >
                {{ getStatusText(props.value) }}
              </q-chip>
            </q-td>
          </template>

          <template v-slot:body-cell-event_name="props">
            <q-td :props="props">
              <q-chip color="blue" text-color="white" size="sm">
                {{ props.value }}
              </q-chip>
            </q-td>
          </template>

          <!-- URL сайта -->
          <template v-slot:body-cell-source_url="props">
            <q-td :props="props">
              <div v-if="props.value" class="url-cell">
                <q-btn
                    :label="formatUrl(props.value)"
                    :href="props.value"
                    target="_blank"
                    flat
                    dense
                    color="primary"
                    icon="link"
                    class="text-left url-link"
                    style="max-width: 150px; text-transform: none;"
                />
                <q-tooltip>{{ props.value }}</q-tooltip>
              </div>
              <div v-else-if="getEventSourceUrl(props.row)" class="url-cell">
                <q-btn
                    :label="formatUrl(getEventSourceUrl(props.row))"
                    :href="getEventSourceUrl(props.row)"
                    target="_blank"
                    flat
                    dense
                    color="secondary"
                    icon="facebook"
                    class="text-left url-link"
                    style="max-width: 150px; text-transform: none;"
                />
                <q-tooltip>FB API: {{ getEventSourceUrl(props.row) }}</q-tooltip>
              </div>
              <span v-else class="text-grey-6">—</span>
            </q-td>
          </template>

          <!-- Качество данных -->
          <template v-slot:body-cell-quality_score="props">
            <q-td :props="props">
              <div v-if="props.value !== null" class="flex items-center justify-center">
                <q-badge
                    :color="getQualityColor(props.value)"
                    :label="props.value + '/10'"
                />
              </div>
              <span v-else class="text-grey-6">—</span>
            </q-td>
          </template>

          <template v-slot:body-cell-actions="props">
            <q-td :props="props">
              <q-btn
                  icon="visibility"
                  size="sm"
                  flat
                  round
                  @click="viewConversionDetails(props.row)"
              >
                <q-tooltip>{{ t('home.recent_conversions.details') }}</q-tooltip>
              </q-btn>
            </q-td>
          </template>
        </q-table>

        <div class="text-center q-mt-md">
          <q-btn
              flat
              icon="visibility"
              :label="t('home.recent_conversions.view_all')"
              to="/facebook-conversions"
          />
        </div>
      </q-card-section>
    </q-card>

    <!-- Диалог подробностей конверсии -->
    <q-dialog v-model="showConversionDialog">
      <q-card style="min-width: 600px">
        <q-card-section>
          <div class="text-h6">{{ t('home.conversion_details.title', { id: selectedConversion?.id }) }}</div>
        </q-card-section>

        <q-card-section v-if="selectedConversion">
          <div class="row q-gutter-md">
            <!-- Основная информация -->
            <div class="col-md-6 col-12">
              <q-list>
                <q-item>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.conversion_id') }}</q-item-label>
                    <q-item-label caption>{{ selectedConversion.id }}</q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.event') }}</q-item-label>
                    <q-item-label caption>{{ selectedConversion.event_name }}</q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.pixel') }}</q-item-label>
                    <q-item-label caption>{{ selectedConversion.pixel_id }}</q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.account') }}</q-item-label>
                    <q-item-label caption>{{ selectedConversion.account_name }}</q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.status') }}</q-item-label>
                    <q-item-label>
                      <q-chip
                          :color="getStatusColor(selectedConversion.status)"
                          text-color="white"
                          size="sm"
                      >
                        {{ getStatusText(selectedConversion.status) }}
                      </q-chip>
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.created_at') }}</q-item-label>
                    <q-item-label caption>{{ selectedConversion.created_at }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>

            <!-- URL и качество данных -->
            <div class="col-md-6 col-12">
              <q-list>
                <!-- URL информация -->
                <q-item v-if="selectedConversion.source_url">
                  <q-item-section avatar>
                    <q-icon name="link" color="primary" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.site_url') }}</q-item-label>
                    <q-item-label caption>
                      <a :href="selectedConversion.source_url" target="_blank" class="text-primary">
                        {{ selectedConversion.source_url }}
                      </a>
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <q-item v-else-if="getEventSourceUrl(selectedConversion)">
                  <q-item-section avatar>
                    <q-icon name="facebook" color="secondary" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.facebook_api_url') }}</q-item-label>
                    <q-item-label caption>
                      <a :href="getEventSourceUrl(selectedConversion)" target="_blank" class="text-secondary">
                        {{ getEventSourceUrl(selectedConversion) }}
                      </a>
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <!-- Качество данных -->
                <q-item v-if="selectedConversion.quality_score !== null">
                  <q-item-section avatar>
                    <q-icon name="star" :color="getQualityColor(selectedConversion.quality_score)" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.data_quality') }}</q-item-label>
                    <q-item-label caption>
                      <q-badge
                          :color="getQualityColor(selectedConversion.quality_score)"
                          :label="selectedConversion.quality_score + '/10'"
                      />
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <!-- Количество идентификаторов -->
                <q-item v-if="selectedConversion.identifiers_count">
                  <q-item-section avatar>
                    <q-icon name="how_to_reg" color="info" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ t('home.conversion_details.user_identifiers') }}</q-item-label>
                    <q-item-label caption>{{ t('home.conversion_details.identifiers_count', {
                      current: selectedConversion.identifiers_count,
                      total: 9
                    }) }}</q-item-label>
                  </q-item-section>
                </q-item>

                <!-- Facebook Trace ID -->
                <q-item v-if="selectedConversion.facebook_trace_id">
                  <q-item-section avatar>
                    <q-icon name="bug_report" color="orange" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>Facebook Trace ID</q-item-label>
                    <q-item-label caption>{{ selectedConversion.facebook_trace_id }}</q-item-label>
                  </q-item-section>
                </q-item>

                <!-- Ошибка если есть -->
                <q-item v-if="selectedConversion.error_message">
                  <q-item-section avatar>
                    <q-icon name="error" color="negative" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-negative">{{ t('home.conversion_details.error') }}</q-item-label>
                    <q-item-label caption class="text-negative">{{ selectedConversion.error_message }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat :label="t('common.close')" @click="showConversionDialog = false" />
          <q-btn
              color="primary"
              :label="t('home.conversion_details.view_details')"
              :to="`/facebook-conversions?id=${selectedConversion?.id}`"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import { useQuasar } from 'quasar'
import api from '@/api/api'
import QuickConversionForm from '@/components/QuickConversionForm.vue'

const $q = useQuasar()
const { t } = useI18n()
const authStore = useAuthStore()

// Reactive data
const stats = ref({
  accounts: 0,
  pixels: 0,
  conversions: 0
})

const recentConversions = ref([])
const loadingConversions = ref(false)
const showConversionDialog = ref(false)
const selectedConversion = ref(null)

// Computed
const user = computed(() => authStore.user || {})

// Columns for recent conversions table
const conversionColumns = computed(() => [
  { name: 'id', label: 'ID', field: 'id', align: 'left' },
  { name: 'event_name', label: t('home.table.event'), field: 'event_name', align: 'left' },
  { name: 'pixel_id', label: t('home.table.pixel'), field: 'pixel_id', align: 'left' },
  { name: 'source_url', label: 'URL', field: 'source_url', align: 'left' },
  { name: 'quality_score', label: t('home.table.quality'), field: 'quality_score', align: 'center' },
  { name: 'status', label: t('home.table.status'), field: 'status', align: 'center' },
  { name: 'created_at', label: t('home.table.created'), field: 'created_at', align: 'left', format: val => new Date(val).toLocaleString('ru-RU') },
  { name: 'actions', label: '', field: 'actions', align: 'center' }
])

// Methods
const fetchStats = async () => {
  try {
    // Получаем аккаунты
    const { data: accounts } = await api.get('facebook-accounts')
    stats.value.accounts = accounts.length

    // Считаем активные пиксели
    let totalPixels = 0
    for (const account of accounts) {
      try {
        const { data: response } = await api.get(`facebook-pixels?account_id=${account.id}`)
        const pixelsList = response.pixels || []
        totalPixels += pixelsList.filter(p => p.status === 'active').length
      } catch (err) {
        console.error('Ошибка загрузки пикселей:', err)
      }
    }
    stats.value.pixels = totalPixels

    // Получаем количество конверсий за сегодня
    const { data: conversions } = await api.get('facebook-conversions?limit=100')
    const today = new Date().toDateString()
    stats.value.conversions = conversions.conversions.filter(c => {
      const conversionDate = new Date(c.created_at).toDateString()
      return conversionDate === today
    }).length

  } catch (err) {
    console.error('Ошибка загрузки статистики:', err)
  }
}

const fetchRecentConversions = async () => {
  loadingConversions.value = true
  try {
    const { data } = await api.get('facebook-conversions?limit=10')
    recentConversions.value = data.conversions
  } catch (err) {
    $q.notify({
      type: 'negative',
      message: t('home.notifications.load_conversions_error') + ': ' + err.message,
      position: 'top'
    })
  } finally {
    loadingConversions.value = false
  }
}

const onConversionSent = (conversionData) => {
  $q.notify({
    type: 'positive',
    message: t('home.notifications.conversion_sent_success', { eventName: conversionData.eventName }),
    position: 'top'
  })

  // Обновляем статистику и список конверсий
  fetchStats()
  fetchRecentConversions()
}

const viewConversionDetails = (conversion) => {
  selectedConversion.value = conversion
  showConversionDialog.value = true
}

// Helper functions
const getRoleText = (role) => {
  return role === 'admin' ? t('users.role.admin') : t('users.role.user')
}

const getStatusColor = (status) => {
  switch (status) {
    case 'success': return 'green'
    case 'failed': return 'red'
    case 'pending': return 'orange'
    default: return 'grey'
  }
}

const getStatusIcon = (status) => {
  switch (status) {
    case 'success': return 'check'
    case 'failed': return 'close'
    case 'pending': return 'schedule'
    default: return 'help'
  }
}

const getStatusText = (status) => {
  const statusMap = {
    success: t('facebook.conversions.status.successful'),
    failed: t('facebook.conversions.status.failed'),
    pending: t('facebook.conversions.status.processing')
  }
  return statusMap[status] || t('common.unknown')
}

const formatUrl = (url) => {
  if (!url) return ''
  try {
    const urlObj = new URL(url)
    return urlObj.hostname + urlObj.pathname
  } catch {
    return url.length > 25 ? url.substring(0, 25) + '...' : url
  }
}

const getQualityColor = (score) => {
  if (score >= 8) return 'positive'
  if (score >= 6) return 'warning'
  if (score >= 4) return 'orange'
  return 'negative'
}

const getEventSourceUrl = (conversion) => {
  return conversion?.custom_data?.event_source_url || null
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchStats(),
    fetchRecentConversions()
  ])
})
</script>

<style scoped>
.url-cell {
  max-width: 150px;
}

.url-link {
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: flex-start;
}
</style>